import { useAudioPrefsStore } from '@listening/frontend/stores/audio-preferences-store';
import {
  guestVoiceAtom,
  userPreferencesAtom,
} from '@listening/frontend/query/user-atoms';
import {
  normalizeVoiceName,
  resolveVoiceInput,
} from '@listening/api-contract/schemas';
import { RegistryContext } from '@effect/atom-react';
import { AsyncResult, type AtomRegistry } from 'effect/reactivity';
import { use, useLayoutEffect, useRef } from 'react';
import { localizeHref } from '@/paraglide/runtime';
import { userLoggedInAtom } from '@/services/auth-service';

export type PublicToolGenerationPrefs = {
  speed?: number;
  /** Voice display name or Kokoro model_id. */
  voice?: string | undefined;
  /** Enable read-along auto-scroll when opening from a converter. */
  autoScroll?: boolean;
  /** Attempt playback on arrival (best-effort; browsers may block). */
  autoplay?: boolean;
};

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5] as const;

export function normalizePublicToolPlaybackSpeed(
  speed: number | undefined,
): number | undefined {
  if (speed === undefined || !Number.isFinite(speed)) return undefined;
  const match = SPEED_OPTIONS.find((option) => option === speed);
  return match ?? undefined;
}

export function buildPublicToolReadAlongHref(
  jobId: string,
  prefs?: PublicToolGenerationPrefs,
): string {
  const path = localizeHref(`/public-tools/readalong/${jobId}`);
  if (typeof window === 'undefined') return path;

  const url = new URL(path, window.location.origin);
  const speed = normalizePublicToolPlaybackSpeed(prefs?.speed);
  if (speed !== undefined && speed !== 1) {
    url.searchParams.set('speed', speed.toString());
  }
  if (prefs?.voice) {
    url.searchParams.set('voice', normalizeVoiceName(prefs.voice));
  }
  if (prefs?.autoScroll) {
    url.searchParams.set('autoscroll', '1');
  }
  if (prefs?.autoplay) {
    url.searchParams.set('autoplay', '1');
  }
  const query = url.searchParams.toString();
  return query ? `${url.pathname}?${query}` : url.pathname;
}

export function parsePublicToolGenerationPrefs(search: {
  readonly speed?: number | undefined;
  readonly voice?: string | undefined;
  readonly autoscroll?: unknown;
  readonly autoplay?: unknown;
}): PublicToolGenerationPrefs {
  const speed = normalizePublicToolPlaybackSpeed(search.speed);

  const voiceRaw = search.voice;
  const voice =
    voiceRaw !== undefined && voiceRaw.trim().length > 0
      ? normalizeVoiceName(voiceRaw)
      : undefined;

  const autoScrollRaw = search.autoscroll;
  const autoScroll =
    autoScrollRaw === '1' ||
    autoScrollRaw === 1 ||
    autoScrollRaw === true ||
    autoScrollRaw === 'true';

  const autoplayRaw = search.autoplay;
  const autoplay =
    autoplayRaw === '1' ||
    autoplayRaw === 1 ||
    autoplayRaw === true ||
    autoplayRaw === 'true';

  return {
    ...(speed !== undefined ? { speed } : {}),
    ...(voice ? { voice } : {}),
    ...(autoScroll ? { autoScroll: true } : {}),
    ...(autoplay ? { autoplay: true } : {}),
  };
}

/** Plays a public tool's read-along in `voice` without saving it to the
 * listener's preferences. */
export function showPublicToolVoice(
  registry: AtomRegistry.AtomRegistry,
  voice: string,
) {
  if (!AsyncResult.getOrElse(registry.get(userLoggedInAtom), () => false)) {
    registry.set(guestVoiceAtom, voice);
    return;
  }
  const old = AsyncResult.getOrElse(
    registry.get(userPreferencesAtom),
    () => undefined,
  );
  registry.set(userPreferencesAtom, {
    most_recent_language: old?.most_recent_language,
    voice,
  });
}

/** Apply voice/speed/auto-scroll from converter URL params once per page load. */
export function useApplyPublicToolGenerationPrefs(
  prefs: PublicToolGenerationPrefs,
) {
  const registry = use(RegistryContext);
  const setPlaybackRate = useAudioPrefsStore((s) => s.setPlaybackRate);
  const setAutoScroll = useAudioPrefsStore((s) => s.setAutoScroll);
  const hasPrefs =
    prefs.speed !== undefined ||
    prefs.voice !== undefined ||
    prefs.autoScroll === true;
  const appliedRef = useRef(false);

  useLayoutEffect(() => {
    if (!hasPrefs || appliedRef.current) return;
    appliedRef.current = true;

    if (prefs.speed !== undefined) {
      setPlaybackRate(prefs.speed);
    }
    if (prefs.autoScroll) {
      setAutoScroll(true);
    }
    // URL params are attacker-controllable; only known voices reach the
    // preferences.
    if (prefs.voice && resolveVoiceInput(prefs.voice)) {
      showPublicToolVoice(registry, normalizeVoiceName(prefs.voice));
    }
  }, [
    hasPrefs,
    prefs.autoScroll,
    prefs.speed,
    prefs.voice,
    registry,
    setAutoScroll,
    setPlaybackRate,
  ]);
}
